import { Injectable } from '@angular/core';
import { HttpRequestService } from '../../utilities/http/request.utilities';
import { environment } from '../../../environments/environment';
import { MatSnackBar } from '@angular/material/snack-bar';
import { InvalidAPIKeyComponent } from '../../components/generic/invalid-api-key/invalid-api-key.component';
import { MatDialog } from '@angular/material/dialog';
import { LocalForageUtilities } from '../../utilities/browser/localForage.utilities';
import {BehaviorSubject, lastValueFrom} from 'rxjs';
import { HttpClient } from '@angular/common/http';
import {storeEquipmentData } from '../../store/equipment/equipment.actions';
import { Store } from '@ngrx/store';

interface EquipmentPayload {
    details: any;       
    resources: any[];
    prompts: any[];
  }

interface IMakeStatistics {
  make: string;
  manual_type?: string;
  total_items: number;
  total_cost: number;
  average_cost: number;
  execution_time_seconds: number;
  items_per_second: number;
  statistics: {
    missing_sn: number;
    missing_model: number;
    linked: number;
    equipment_cost: {
      missing_sn: number;
      missing_model: number;
      linked: number;
    };
  };
}

@Injectable({
  providedIn: 'root',
})


export class EquipmentService {
  // Creating BehaviorSubjects that will be shared across multiple components
  private sharedPVCMakesSource = new BehaviorSubject<string[]>([]);
  private sharedSelectedMakeSource = new BehaviorSubject<string>('');
  // First shared resource will be the pvc makes 
  sharedPVCMakes$ = this.sharedPVCMakesSource.asObservable();
  // Second shared resource is the selected make in resource dashboard
  sharedSelectedMake$ = this.sharedSelectedMakeSource.asObservable();
  // This function will update the value for PVC makes 
  setValue(value: string[]) {
    this.sharedPVCMakesSource.next(value);
  }
  // This function will update the value for selected make
  setNewValue(value: string) {
    this.sharedSelectedMakeSource.next(value);
  }
  // This will contain the PVC makes after the admin makes API call
  pvcMakes: string[] = [];
  hasPdf = true;
  hasPdfByMake = true;
  loading = new BehaviorSubject<boolean>(false);
  error = new BehaviorSubject<boolean>(false);

  private endpoints: { [key: string]: string } = {
    make: environment.integrations.adminMake,
    model: environment.integrations.model,
    serialNumber: environment.integrations.serialNumber,
    makeStats: environment.integrations.statistics.makes,
    equipmentDetails: environment.integrations.equipmentDetails
  }

  public makes: any[] = [];
  public makesStatistics: any[] = [];

  constructor(private store: Store, private httpRequestService: HttpRequestService,  private snackBar: MatSnackBar,private dialog: MatDialog,private http: HttpClient, private localForage: LocalForageUtilities) { 
  }

  /**
   * Fetches and formats statistics for makes within a specified range based on the manual type.
   * @param start The starting index for fetching makes.
   * @param end The ending index for fetching makes.
   * @param manual The type of manual to filter statistics by.
   * @returns A promise that resolves to the formatted statistics data.
   */
  public async getMakesStatistics(start: number, end: number, make?: string, manualType?: string): Promise<any>
  {
    try {
      // Fetch all makes statistics within the specified range
      const data = await this.fetchAllMakesStatistics(start, end, make, manualType);

      // Format the fetched data
      const formattedData = this.formatMakesStatistics(data);

      return formattedData;
    } catch (error) {
      // Log and return an empty array on error
      console.error('Error fetching makes statistics:', error);
      return [];
    }
  }

  /**
   * Formats the makes statistics data based on the specified manual type.
   * @param data Array of makes statistics data.
   * @param manual The type of manual to use for formatting the data.
   * @returns An array of formatted statistics data.
   */
  private formatMakesStatistics(data: IMakeStatistics[]): any {
    return data?.map((make: IMakeStatistics) => {
    const linked = make.statistics?.linked || 0;
    const missingNoModel = make.statistics?.missing_model || 0;
    const missingNoSN = make.statistics?.missing_sn || 0;

    const linkedCost = make.statistics?.equipment_cost?.linked || 0;
    const missingNoModelCost = make.statistics?.equipment_cost?.missing_model || 0;
    const missingNoSNCost = make.statistics?.equipment_cost?.missing_sn || 0;

    const totalOEC = linkedCost + missingNoModelCost + missingNoSNCost;
    const totalCount = linked + missingNoModel + missingNoSN;

    const percentage = (part: number, total: number) => total === 0 ? '0%' : ((part / total) * 100).toFixed(2) + '%';

    return {
      make: make.make,
      PDFLinkedFlags: {
        linked: { value: 'Manuals Linked' },
        noModel: { value: 'Manual not available for Model' },
        noSN: { value: 'Manual not available for SN' }
      },
      equipmentCost: {
        linked: { value: new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(linkedCost) },
        noModel: { value: new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(missingNoModelCost) },
        noSN: { value: new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(missingNoSNCost) }
      },
      percentageOfTotalOEC: {
        linked: { value: percentage(linkedCost, totalOEC) },
        noModel: { value: percentage(missingNoModelCost, totalOEC) },
        noSN: { value: percentage(missingNoSNCost, totalOEC) }
      },
      distinctEquipmentCodeCount: {
        linked: { value: linked.toString() },
        noModel: { value: missingNoModel.toString() },
        noSN: { value: missingNoSN.toString() }
      },
      percentageOfTotalCount: {
        linked: { value: percentage(linked, totalCount) },
        noModel: { value: percentage(missingNoModel, totalCount) },
        noSN: { value: percentage(missingNoSN, totalCount) }
      }
    };
    }) || [];
  }

  /**
   * Fetches statistics for all makes within a specified range.
   * @param start The starting index of the makes to fetch.
   * @param end The ending index of the makes to fetch.
   */
  private async fetchAllMakesStatistics(start: number, end: number, make?: string, manualType?: string): Promise<any> {
    this.makesStatistics = [];
    if (make) {
      // Fetch statistics for a single make
      return new Promise((resolve, reject) => {
        const query = manualType ? `?make=${make}&manual_type=${manualType}` : `?make=${make}`;
        this.httpRequestService.request<any>('GET', `${this.endpoints['makeStats']}${query}`).subscribe({
          next: (response) => {
            this.makesStatistics.push(response);
            resolve([response]);  // Wrap in array to maintain the expected format
          },
          error: (error) => {
           if (error.status === 404) {
            const errorMessage = error.error?.detail || `No equipment data found for make: ${make}`;
            this.snackBar.open(errorMessage, 'Close', {
                duration: 5000,
                panelClass: ['warning-snackbar'],
                horizontalPosition: 'center',
                verticalPosition: 'top'
              }); // Show alert snackbar directly from service
            resolve([]); 
          } else {
            console.error('Error fetching statistics for make:', make, error);

            this.snackBar.open('Error fetching statistics for make: ' + make, 'Close', {
              duration: 5000,
              panelClass: ['error-snackbar'],
              horizontalPosition: 'center',
              verticalPosition: 'top'
            });
            reject(error);
          }
        }
        });
      });
    } else {
      // Fetch all makes from the server
      return this.fetchAllMakes().then(() => {
        // Retrieve the makes within the specified range
        const makes = this.getMakes(start, end);
       
        // For each make, fetch the statistics from the server
        return Promise.all(makes.map(make => {
          return new Promise((resolve, reject) => {
            this.httpRequestService.request<any>('GET', `${this.endpoints['makeStats']}?make=${make}`).subscribe({
              next: (response) => {
                this.makesStatistics.push(response);
                resolve(response);
              },
              error: (error) => {
                console.error('Error fetching statistics for make:', make, error);
                reject(error);
              }
            });
          });
        }));
      }).catch(error => {
        console.error('Error fetching all makes:', error);
      });
    }
  }

  /**
   * Retrieves a subset or all makes from the stored list based on the provided indices.
   * If no indices are provided, it returns the entire list of makes.
   * 
   * @param {number} [start] - The starting index from which to begin slicing the makes array.
   * @param {number} [end] - The ending index at which to stop slicing the makes array.
   * @returns {string[]} A subset of the makes array from start index to end index, or the entire array if no indices are provided.
   */
  public getMakes(start?: number, end?: number, make?: string): string[] {
    if (!start && !end && !make) {
      return this.makes;
    }
    if (make) {
      return this.makes.filter(m => m === make);
    }
    return this.makes.slice(start, end);
  }


  /**
   * Generates the manual download urls and gets ready for PDF load
   */
  async prepareEquipmentData(equipmentData: EquipmentPayload): Promise<void> {
    this.hasPdf = true;
    this.hasPdfByMake = true;

    const resources = await Promise.all(
      equipmentData.resources.map(resource => this.fetchDownloadUrl(resource.providerResourceUrl || ''))
    );
    const equipment = {
      ...equipmentData,
      resources: equipmentData.resources.map((resource, index) =>
        ({ ...resource, downloadUrl: resources[index].downloadUrl, s3Key: resources[index].s3Key }))
    };

    const pdfResponse = equipment.resources.some(resource =>
      resource.s3Key && resource.s3Key.endsWith('.pdf') 
    );

    const pdfResources = resources.filter(resource =>
      resource.downloadUrl && resource.downloadUrl.length > 0
    );

    await this.localForage.cleanupExpired();
    const maxSafeSizeInMB = 10;
    for (const resource of pdfResources) {
      const pdfUrl = resource.downloadUrl;
      const pdfName = new URL(pdfUrl).pathname;

      try {
        // Check file size first using HEAD request
        const headResponse = await lastValueFrom(
          this.http.head(pdfUrl, { observe: 'response' })
        );

        const contentLength = headResponse.headers.get('content-length');
        const fileSizeMB = contentLength ? parseInt(contentLength, 10) / (1024 * 1024) : 0;

        // Only download if file size is under the limit
        if (fileSizeMB < maxSafeSizeInMB) {
          const blob = await lastValueFrom(this.http.get(pdfUrl, { responseType: 'blob' }));
          await this.localForage.set(pdfName, blob, 1000 * 60 * 60 * 4); // 4 hours
        }
      } catch (error) {
        console.error('Error handling PDF:', error);
      }
    }

    if (equipment.details.serialNumberRange){
      this.hasPdfByMake = false;
    }else {
      this.hasPdf = false;
    }

    if (equipment.details.serialNumberRange){
      this.hasPdfByMake = false;
    } else {
      this.hasPdf = false;
    }

    this.store.dispatch(storeEquipmentData({ equipment }));
    this.loading.next(false);

    if (!pdfResponse) {
      return;
    }
  }

  private fetchDownloadUrl(providerResourceUrl: string): Promise<{ downloadUrl: string, s3Key: string }> {
    const sanitizedUrl = providerResourceUrl.replace(/;/g, '%3B');
    return new Promise((resolve) => {
      this.httpRequestService.request<any>('GET', sanitizedUrl).subscribe({
        next: (response) => {
          resolve({ downloadUrl: response.download_url, s3Key: response.s3_key });
        },
        error: (error) => {
          console.error(`Error fetching download URL: ${error.status}`, error);
          if (error.status === 404) {
            this.error.next(true);
            this.loading.next(false);
            resolve({ downloadUrl: '', s3Key: '' });
          } else {
            resolve({ downloadUrl: '', s3Key: '' });
          }
        }
      });
    });
  }

  /**
   * Fetches equipment details from the transaction_lambda_rds /technician/equipment_details endpoint
   * @param equipmentCode The equipment code to fetch details for
   * @returns A promise that resolves to the equipment details data
   */
  public async getEquipmentDetails(equipmentCode?: string): Promise<any> {
    return new Promise((resolve, reject) => {
      const query = equipmentCode ? `?equipment_code=${equipmentCode}` : '';
      this.httpRequestService.request<any>('GET', `${this.endpoints['equipmentDetails']}${query}`).subscribe({
        next: (response) => {
          resolve(response);
        },
        error: (error) => {
          if (error.status === 404) {
            const errorMessage = error.error?.detail || `No equipment details found${equipmentCode ? ` for code: ${equipmentCode}` : ''}`;
            this.snackBar.open(errorMessage, 'Close', {
              duration: 5000,
              panelClass: ['warning-snackbar'],
              horizontalPosition: 'center',
              verticalPosition: 'top'
            });
            resolve(null);
          } 
          else if (error.status === 401){
            this.dialog.open(InvalidAPIKeyComponent);
            console.warn('Wrong API key');
            resolve(null);
          }
          else {
            console.error('Error fetching equipment details:', error);
            this.snackBar.open(`Error fetching equipment details${equipmentCode ? ` for code: ${equipmentCode}` : ''}`, 'Close', {
              duration: 5000,
              panelClass: ['error-snackbar'],
              horizontalPosition: 'center',
              verticalPosition: 'top'
            });
            reject(error);
          }
        }
      });
    });
  }

  /**
   * Fetches all makes from the backend.
   * @returns A promise that resolves to the response from the backend.
   */
  public fetchAllMakes(): Promise<{makes: string[]}> {
    return new Promise((resolve, reject) => {
      this.httpRequestService.request<{makes: string[], pvc_makes: string[]}>('GET', this.endpoints['make']).subscribe({
        next: (response) => {
          this.pvcMakes = response.pvc_makes; //Assign the PVC makes from the makes API call
          this.setValue(this.pvcMakes); //Set the value in shared variable
          this.makes = response.makes;
          resolve(response);  
        },
        error: (error) => reject(error)
      });
    });
  }


}
